import React, { useEffect, useState } from "react";
import Footer from "../components/Footer";
import NavBar from "../components/NavBar";
import FilesUploadBox from "../components/uploadFiles/FilesUploadBox";
import { useLocation, useNavigate } from "react-router-dom";
import UploadModeSelector from "../components/uploadFiles/UploadModeSelector";
import PrintOptions from "../components/uploadFiles/PrintOptions";
import CustomerDetailsBox from "../components/uploadFiles/CustomerDetailsBox";
import { useSearchParams } from "react-router-dom";
import { equalTo, get, orderByChild, push, query, ref as databaseRef } from "firebase/database";
import { jobRequestsDbRef } from "../utils/dbRefs";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { database, storage } from "../firebase/firebaseConfig";
import { authenticatedJson, ensureGuestSession } from "../utils/guestSession";
import { functionsBaseUrl } from "../config/runtime";

function UploadFiles() {
    const location = useLocation();
    const navigate = useNavigate();
    const [files, setFiles] = useState([]);
    const [step, setStep] = useState(1);
    const [selectedMode, setSelectedMode] = useState(null);
    const [searchParams] = useSearchParams();

    const shopCode = searchParams.get("shopCode");

    const [selectedShop, setSelectedShop] = useState(location.state?.selectedShop);
    const [loading, setLoading] = useState(true);
    const [isSent, setIsSent] = useState(false);
    const [isSending, setIsSending] = useState(false);


    useEffect(() => {
        if (!shopCode) {
            setLoading(false);
            return;
        }

        const fetchShop = async () => {
            console.log("Fetching shop with code:", shopCode); // Debug log
            try {
                const shopQuery = query(
                    databaseRef(database, "PublicStores"),
                    orderByChild("shopCode"),
                    equalTo(shopCode)
                );

                const snapshot = await get(shopQuery);

                if (snapshot.exists()) {
                    const data = snapshot.val();

                    const firstShop = Object.entries(data)[0];

                    if (firstShop) {
                        const [uid, shopData] = firstShop;

                        setSelectedShop({
                            uid,
                            ...shopData,
                        });
                    } else {
                        setSelectedShop(null);
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchShop();
    }, [shopCode]);

    const handleBack = () => {
        navigate(-1);
    };

    const handleSubmit = async (customer) => {
        try {
            setIsSending(true);

            // 1. Create DB ref first
            const newRef = push(jobRequestsDbRef);
            const jobId = newRef.key;
            const guest = await ensureGuestSession();

            // 2. Upload files to Firebase Storage
            const uploadedFiles = await Promise.all(
                files.map(async (f) => {
                    const filePath = `jobRequests/${guest.uid}/${jobId}/${f.file.name}`;
                    const storageRef = ref(storage, filePath);

                    // upload file
                    await uploadBytes(storageRef, f.file);

                    // get URL
                    const url = await getDownloadURL(storageRef);

                    return {
                        name: f.file.name,
                        type: f.file.type,
                        size: f.file.size,
                        pageCount: f.pageCount || 1,
                        url, // ✅ IMPORTANT
                        storagePath: filePath,
                        printOptions: f.printOptions,
                    };
                })
            );

            await authenticatedJson(`${functionsBaseUrl}/submitPrintJob`, {
                jobId,
                shopUid: selectedShop.uid,
                customer: {
                    name: customer.name || "",
                    email: customer.email,
                    phone: customer.phone || "",
                },
                files: uploadedFiles,
            });

            console.log("Job Request Created:", jobId);

            // ✅ Success only after EVERYTHING succeeds
            setIsSent(true);
        } catch (err) {
            console.error("Error creating job request:", err);
            setIsSent(false);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="bg-background min-h-full flex flex-col font-['Plus_Jakarta_Sans',sans-serif]">
            <NavBar />
            <section className="flex-1 flex flex-col ">

                <div className="flex-1 flex items-start justify-center p-[16px] md:p-[40px]">

                    {/* STEP 1: UPLOAD */}
                    {step === 1 && (
                        <FilesUploadBox
                            loading={loading}
                            selectedShop={selectedShop}
                            files={files}
                            setFiles={setFiles}
                            selectedMode={selectedMode}
                            onBack={handleBack}
                            onContinue={() => setStep(2)}
                        />
                    )}

                    {/* STEP 2: MODE SELECT */}
                    {step === 2 && (
                        <UploadModeSelector
                            selectedMode={selectedMode}
                            setSelectedMode={setSelectedMode}
                            setFiles={setFiles}
                            onBack={() => setStep(1)}
                            onContinue={() => {
                                setStep(selectedMode === "custom" ? 3 : 4)
                            }}
                        />
                    )}

                    {/* STEP 3: PRINT OPTIONS */}
                    {step === 3 && selectedMode === 'custom' && (
                        <PrintOptions
                            files={files}
                            setFiles={setFiles}
                            onBack={() => setStep(2)}
                            onContinue={() => setStep(4)}
                        />
                    )}

                    {/* STEP 4: CUSTOMER DETAILS */}

                    {step === 4 && (
                        <CustomerDetailsBox
                            selectedShop={selectedShop}
                            files={files}
                            selectedMode={selectedMode}
                            onBack={() =>
                                setStep(selectedMode === "custom" ? 3 : 2)
                            }
                            onSubmit={(customerData) => {
                                handleSubmit(customerData);
                            }}
                            isSent={isSent}
                            isSending={isSending}
                            onStartNewOrder={() => {
                                setFiles([]);
                                setSelectedMode(null);
                                setStep(1);
                                setIsSent(false);
                                setIsSending(false);
                            }}
                        />
                    )}

                </div>

            </section>

        </div>
    );
}

export default UploadFiles;
